import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import useProducts, {
  ProductsFilter,
} from '../../../hooks/api/queries/useProducts';

import useBankroll from '../../../hooks/api/queries/useBankroll';
import { useFiltersContext } from '../../../hooks/context/useFiltersContext';
import { breakpoint } from '../../../styles/constants';
import { EmptyPlaceholder } from '../../ui/notFound/EmptyPlaceholder';
import { Pagination } from './Pagination';
import { ProductCard } from './ProductCard';

interface ProductsProps {
  categoryId: number;
  categorySlug?: string;
}

export const Products = ({ categoryId, categorySlug }: ProductsProps) => {
  const [activePage, setActivePage] = useState<number>(1);
  const { filters, refetch, subCatList } = useFiltersContext();
  const { data: balance } = useBankroll();

  const [productsFilter, setProductsFilter] = useState<ProductsFilter>({
    categoryId,
    subCatList,
    attributes: filters.attributes.map((attr) => attr.id),
    attrKeyCount: Array.from(
      new Set(filters.attributes.map((attr) => attr.keyId)),
    ).length,
    sort: [filters.sorting],
    minPrice: filters?.range?.min,
    maxPrice: filters.onlyAvailable ? balance : filters.range?.max,
    skip: (activePage - 1) * 9,
  });

  const { data } = useProducts(productsFilter);

  useEffect(() => {
    if ((activePage - 1) * 9 !== productsFilter.skip)
      setProductsFilter({ ...productsFilter, skip: (activePage - 1) * 9 });
  }, [activePage, productsFilter]);
  useEffect(() => {
    if (filters.onlyAvailable && productsFilter)
      setProductsFilter({
        ...productsFilter,
        maxPrice:
          productsFilter?.maxPrice && productsFilter.maxPrice < (balance ?? 0)
            ? productsFilter.maxPrice
            : balance,
      });
  }, [balance, filters.onlyAvailable, productsFilter]);

  useEffect(() => {
    if (refetch === true) {
      setActivePage(1);
      setProductsFilter({
        categoryId,
        subCatList,
        attributes: filters.attributes.map((attr) => attr.id),
        attrKeyCount: Array.from(
          new Set(filters.attributes.map((attr) => attr.keyId)),
        ).length,
        minPrice: filters?.range?.min,
        maxPrice: filters.range?.max,
        sort: [filters.sorting],
        skip: 0,
      });
    }
  }, [filters, balance, refetch, subCatList, categoryId]);

  if (!data) return null;

  return (
    <Wrapper>
      {data.total > 0 ? (
        <>
          <ProductsWrapper>
            {data.results.map((product) => (
              <li key={product.id}>
                  <ProductCard product={product} categorySlug={categorySlug} />
              </li>
            ))}
          </ProductsWrapper>
          {data.total > 9 && (
            <Pagination
              pages={data.total / 9 < 1 ? 1 : Math.ceil(data.total / 9)}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          )}
        </>
      ) : (
        <EmptyPlaceholder type="product" />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ProductsWrapper = styled.ul`
  --gap: 1.25rem;
  padding-top: 0.625rem;
  display: flex;
  flex-direction: column;
  gap: var(--gap);

  flex-direction: row;
  flex-wrap: wrap;

  > * {
    width: 100%;
  }

  @media (min-width: 720px) {
    > * {
      width: calc(50% - 1.25rem / 2);
    }
  }

  @media (min-width: ${breakpoint}) {
    padding-top: 1.25rem;
    > * {
      width: calc(100% / 3 - var(--gap) * 2 / 3);

      overflow: hidden;
    }
  }
`;
